import { useState, useCallback, useMemo, useEffect, Suspense, lazy, useRef } from 'react';
import styles from './LessonLibrary.module.scss';
import {
  useSearchLessonLibraryLazyQuery,
  useGetProgramLazyQuery,
} from '@api/graphql-codegen';
import Spinner from 'src/components/spinner/Spinner';
import Header from 'src/components/header/Header';
import { Events, useAnalytics, useQueryParams, useUser } from '@hooks';
import CloseIcon from 'src/assets/icons/close.svg?react';
import IconRotating from 'src/assets/icons/rotating_arrow.svg?react';
import { LessonLibraryEnum } from '@types';
import { useHistory } from 'react-router-dom';
import {
  standaloneLessonsSelCompetencies,
  categoryTiersOptions,
  naAllGradeLevels,
  lessonTypeOptions,
  ProgramID,
  auAllYearLevels,
  auGettingStartedGradeLevels,
  newGettingStartedGradeLevels,
} from '@constants';
import NavLayout from './navLayout/NavLayout';
import SearchLayout from './searchLayout/SearchLayout';
import { hasArrayValues, resetGoogleTranslateWidget, sortByPosition } from '@src/utils/helpers';
import Button from '@src/components/button/Button';
import { debounce } from '@src/utils/helpers';
import _ from "lodash";
import Programs from './Programs';
const GradeItem = lazy(() => import('./gradeItem/GradeItem'));
const GradeSearchItem = lazy(() => import('./gradeItem/GradeSearchItem'));

type SearchStates = {
  searchText: string[]
  lessonType: string[]
  competency: string[]
  tier: string[]
  skill: string[]
  mood: string[]
}

const defaultStates: SearchStates = {
  searchText: [],
  lessonType: [],
  competency: [],
  tier: [],
  skill: [],
  mood: [],
}

function LessonLibrary() {
  const history = useHistory();
  const params = useQueryParams();
  const [user] = useUser();
  const { trackEvent } = useAnalytics();
  const tab = params.get('tab');
  const searchKeyword = params.get('search-keyword');
  const [states, setStates] = useState<SearchStates>(defaultStates)
  const [filters, setFilters] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [grades, setGrades] = useState<string[]>([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [lessonLibraryMode, setMyCourseMode] = useState(tab === 'programs' ? LessonLibraryEnum.Programs : LessonLibraryEnum.Library)

  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [focused, setFocused] = useState(false);
  const [showGradeMenu, setShowGradeMenu] = useState(false);
  const [showLessonTypeMenu, setShowLessonTypeMenu] = useState<boolean>(false);
  const [showTierMenu, setShowTierMenu] = useState(false);
  const [showCompetencyMenu, setShowCompetencyMenu] = useState(false);
  const [showSkillMenu, setShowSkillMenu] = useState(false);
  const [showMoodMenu, setShowMoodMenu] = useState(false);
  const [loadOtherMCs, setLoadOtherMCs] = useState<number | null>(null);
  const urlKeywordProcessedRef = useRef(false);

  useEffect(() => {
    resetGoogleTranslateWidget();
  }, [])
  const isAUuser = useMemo(() => { return user?.id?.startsWith("AU_") ? true : false }, [user?.id]);
  const gradeOptions = useMemo(() => {
    const grades = JSON.parse(user?.schools?.[0]?.grades || '[]');
    const sortedGrades = grades.sort((a, b) => {
      if (isAUuser) {
        return auGettingStartedGradeLevels.indexOf(a) - auGettingStartedGradeLevels.indexOf(b);
      }
      return newGettingStartedGradeLevels.indexOf(a) - newGettingStartedGradeLevels.indexOf(b);
    })
    return sortedGrades.map(grade => {
      return {
        label: grade,
        value: grade
      }
    })
  }, [user, isAUuser])

  const [getProgram, { data, loading }] = useGetProgramLazyQuery({ fetchPolicy: 'network-only' });
  const [searchLessonLibrary, { data: searchData, loading: searching }] = useSearchLessonLibraryLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      if (data.searchLessonLibrary) {
        trackEvent(Events.actions.lessonLibrarySearch, {
          ...data.searchLessonLibrary,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      }
    }
  });

  const debouncedApiCall = useRef(
    debounce((states: SearchStates) => {
      if (!_.isEqual(states, defaultStates)) {
        return searchLessonLibrary({ variables: { input: states } })
      }
    }, 500)
  ).current;

  // Scroll to the top of the list when the component mounts
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      setTimeout(() => {
        root.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, []);

  useEffect(() => {
    history.replace(`/lessons?tab=${lessonLibraryMode === LessonLibraryEnum.Programs ? 'programs' : 'library'}`);
  }, [lessonLibraryMode, history])

  useEffect(() => {
    document.body.style.overflow = showFilterMenu ? "hidden" : "auto";
    document.getElementById("lessonlibrary")!.style.overflow = showFilterMenu ? "hidden" : "auto";
  }, [showFilterMenu])

  useEffect(() => {
    if (tab === 'programs') {
      setMyCourseMode(LessonLibraryEnum.Programs)
    } else if (tab === 'library') {
      setMyCourseMode(LessonLibraryEnum.Library);

      if (isAUuser) {
        getProgram({ variables: { input: { id: ProgramID.AUSLESSONLIBRARY } } });
      } else {
        getProgram({ variables: { input: { id: ProgramID.LESSONLIBRARY } } });
      }
    }
  }, [tab, isAUuser, getProgram])

  useEffect(() => {
    const listViewElement = document.getElementById("lessonListView")
    if (listViewElement) {
      setTimeout(() => {
        listViewElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 500)
    }
  }, [showFilterMenu])

  const handleSelect = useCallback((gradeId: string) => {
    if (selectedId === gradeId) {
      setSelected(false);
      setSelectedId(null);
    } else {
      setSelected(true);
      setSelectedId(gradeId);
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }, [selectedId, setSelected, setSelectedId])

  const handleNoneSelect = (gradeId?: string) => {
    setSelected(false);
    setSelectedId(null);

    if (gradeId) {
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }

  const handleBackToTop = (gradeId?: string) => {
    if (gradeId) {
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }

  const handleSelectPlanMode = useCallback((planMode: any) => {
    setMyCourseMode(planMode);
    setShowFilterMenu(false);
  }, [setShowFilterMenu]);

  const handleResetFilters = useCallback(() => {
    setFilters([]);
    setGrades([]);
    setSearch('')
    setStates(defaultStates);
  }, []);

  // Handle search keyword from URL parameter (only on first load)
  useEffect(() => {
    if (searchKeyword && searchKeyword.trim() && !urlKeywordProcessedRef.current) {
      const keyword = searchKeyword.trim();

      // Mark as processed so it only runs once
      urlKeywordProcessedRef.current = true;

      // Set the search input value
      setSearch(keyword);

      // Update states with the keyword
      const newStates = {
        ...defaultStates,
        searchText: [keyword],
      };
      setStates(newStates);

      // Add to filters
      const filterOptions = [`Keyword: ${keyword}`];
      setFilters(filterOptions);

      // Ensure we're in library mode for search results
      if (lessonLibraryMode !== LessonLibraryEnum.Library) {
        setMyCourseMode(LessonLibraryEnum.Library);
      }

      // Trigger the search immediately (not debounced for URL-based searches)
      if (!_.isEqual(newStates, defaultStates)) {
        searchLessonLibrary({ variables: { input: newStates } });
      }
    }
  }, [searchKeyword, searchLessonLibrary, lessonLibraryMode])

  const handleShowFilters = () => {
    document.getElementById("lessonListView")?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    // Restore search value from filters if there are keyword filters and search is empty
    const keywordFilters = filters.filter(f => f.includes('Keyword:'));
    if (keywordFilters.length > 0 && !search) {
      // Get the last keyword filter and restore it to the search input
      const lastKeyword = keywordFilters[keywordFilters.length - 1].replace('Keyword: ', '').trim();
      if (lastKeyword) {
        setSearch(lastKeyword);
      }
    } else if (filters.length === 0 || keywordFilters.length === 0) {
      // Only clear search if there are no filters or no keyword filters
      setSearch("")
    }
    setTimeout(() => setShowFilterMenu(current => !current), 300)
  }

  const handleChangeKeyword = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleStatesChange = useCallback((key: string, value: any) => {
    setStates((prev) => {
      const newStates = {
        ...prev,
        [key]: value,
      }
      return newStates
    })
  }, [])

  const handleRemoveFilters = useCallback((value: string[]) => {
    const ageGroups = value.filter(group => group.includes('Grade:'))
    const ageTemp = ageGroups.map(group => {
      return group.replace('Grade: ', '')
    })
    setGrades(ageTemp)

    const changedLessonTypes = value.filter(f => f.includes('LessonType:'))
    const lessonTypeTemp = changedLessonTypes.map(lt => {
      return lt.replace('LessonType: ', '')
    })
    const lessonTypes = lessonTypeTemp.map((ltLabel) => {
      return lessonTypeOptions.find(lt => lt.label === ltLabel)?.value ?? ''
    })
    handleStatesChange('lessonType', lessonTypes ? lessonTypes : [])

    const chagedSkills = value.filter(group => group.includes('Skill:'))

    const skillTemp = chagedSkills.map(group => {
      return group.replace('Skill: ', '')
    })
    const skills = skillTemp.map((skillLabel) => {
      return standaloneLessonsSelCompetencies.find(skill => skill.label === skillLabel)?.value ?? ''
    })
    handleStatesChange('competency', skills ? skills : [])

    handleStatesChange('skill', skillTemp)

    const changedTopics = value.filter(group => group.includes('Tier:'))
    const temp = changedTopics.map(group => {
      return group.replace('Tier: ', '')
    })
    handleStatesChange('tier', temp)

    const changedMood = value.filter(group => group.includes('Mood:'))
    const moodTemp = changedMood.map(group => {
      return group.replace('Mood: ', '')
    })
    handleStatesChange('mood', moodTemp)

    const changedKeyword = value.filter(group => group.includes('Keyword:'))
    const tempKey = changedKeyword.map(group => {
      return group.replace('Keyword: ', '')
    })
    handleStatesChange('searchText', tempKey)

    if (value.length === 0) {
      handleResetFilters();
    } else {
      const searchText = value.filter(v => v.includes('Keyword:')).map(key => key.replace('Keyword:', '').trim());
      const competency = value.filter(v => v.includes('Skill:')).map(key => key.replace('Skill:', '').trim());
      const tiers = value.filter(v => v.includes('Tier:')).map(key => key.replace('Tier:', '').trim());
      const skill = value.filter(v => v.includes('Skill:')).map(key => key.replace('Skill:', '').trim());
      const mood = value.filter(v => v.includes('Mood:')).map(key => key.replace('Mood:', '').trim());
      const newSearch = {
        searchText,
        competency: isAUuser ? [] : competency,
        lessonType: lessonTypes ?? [],
        tier: tiers,
        skill: isAUuser ? skill : [],
        mood: mood,
      }
      setFilters(value);
      debouncedApiCall(newSearch);
    }
  }, [handleStatesChange, handleResetFilters, debouncedApiCall, isAUuser])

  const handleFilterApply = useCallback((keywordValue?: string) => {
    const searchValue = keywordValue?.trim() || search.trim();
    let filterOptions: string[] = []
    if (grades.length) {
      grades.forEach(ageGroup => {
        filterOptions.push(`Grade: ${ageGroup}`)
      })
    }
    if (states.lessonType.length) {
      const lessonTypes = states.lessonType.map((ltValue) => {
        return lessonTypeOptions.find(lt => lt.value === ltValue)?.label
      })
      lessonTypes.forEach(type => {
        filterOptions.push(`LessonType: ${type}`)
      })
    }
    if (states.competency.length) {
      const skills = states.competency.map((skillValue) => {
        return standaloneLessonsSelCompetencies.find(skill => skill.value === skillValue)?.label
      })
      skills.forEach(skill => {
        filterOptions.push(`Skill: ${skill}`)
      })
    }
    if (states.tier.length) {
      states.tier.forEach(topic => {
        filterOptions.push(`Tier: ${topic}`)
      })
    }
    if (states.skill.length) {
      states.skill.forEach(topic => {
        filterOptions.push(`Skill: ${topic}`)
      })
    }
    if (states.mood.length) {
      states.mood.forEach(topic => {
        filterOptions.push(`Mood: ${topic}`)
      })
    }
    if (hasArrayValues(states.searchText)) {
      states.searchText?.forEach(keyword => {
        filterOptions.push(`Keyword: ${keyword}`)
      })
    }
    if (searchValue) {
      if (!states.searchText?.includes(searchValue)) {
        handleStatesChange('searchText', [...states.searchText, searchValue]);
        filterOptions.push(`Keyword: ${searchValue}`);
      }
    }
    setFilters(filterOptions);
    setShowFilterMenu(false);

    // Preserve the search input value so it's visible when opening the filter menu again
    // Only clear if there's no search keyword in the filters
    if (!searchValue || filterOptions.filter(f => f.includes('Keyword:')).length === 0) {
      setSearch("");
    } else {
      // Keep the search value in the input field
      setSearch(searchValue);
    }

    const keywords = filterOptions.filter(filt => filt.includes('Keyword: ')).map(keyword => keyword.replace('Keyword: ', ''))

    var newStates = {
      ...states,
      'searchText': keywords
    }

    debouncedApiCall(newStates)
  }, [grades, states, search, handleStatesChange, debouncedApiCall])

  const searchViewStyle = useMemo(() => {
    const classes = [styles.searchView];
    if (showFilterMenu) {
      classes.push(styles.filterShow);
    }
    return classes.join(' ');
  }, [showFilterMenu])

  const mainViewStyle = useMemo(() => {
    const classes = [styles.libraryView];
    return classes.join(' ');
  }, [])

  const isHasLessonLibrary = useMemo(() => {
    if (user?.id?.startsWith("AU_")) {
      if (user?.schools?.[0]?.programs?.includes("CvsCvxSsD-E0Vv9ea1q1i")) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, [user?.id, user?.schools?.[0]?.programs])

  const handleSelectGrade = useCallback((grades: string[]) => {
    setGrades(grades);
  }, [])

  const handleRemoveTag = (removedTag: string) => {
    const newTags = filters.filter((tag) => tag !== removedTag);
    handleRemoveFilters(newTags);

    // Remove search-keyword from URL if keyword tag is removed
    if (removedTag.includes('Keyword:') && searchKeyword) {
      const tabParam = lessonLibraryMode === LessonLibraryEnum.Programs ? 'programs' : 'library';
      history.replace(`/lessons?tab=${tabParam}`);
    }
  };

  const handleClickDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFilterMenu(false);
  }

  const programList = useMemo(() => {
    if (!data?.getProgram?.mcs) return [];
    var filterList = [...(data?.getProgram.mcs ?? [])].sort(sortByPosition);
    if (grades.length && !showFilterMenu) {
      filterList = filterList.filter(m => grades.includes(m?.name ?? ""))
    }
    return filterList
  }, [grades, showFilterMenu, data?.getProgram])

  const searchedList = useMemo(() => {
    if (!searchData?.searchLessonLibrary) return []
    var filterList = [...(searchData?.searchLessonLibrary ?? [])].sort(sortByPosition)
    if (grades.length && !showFilterMenu) {
      filterList = filterList.filter(m => grades.includes(m?.mCollectionName ?? ""))
    }
    return filterList;
  }, [grades, showFilterMenu, searchData?.searchLessonLibrary])

  const hasOnlyGradeFilters = useMemo(() => {
    return filters.every(item => item.includes('Grade:'));
  }, [filters])

  const lessonListView = (
    <div style={{ width: '100%', height: '100%' }}>
      {user &&
        <NavLayout
          showFilterMenu={showFilterMenu}
          studyMode={lessonLibraryMode}
          handleSelectPlanMode={handleSelectPlanMode}
          handleFilterClick={handleShowFilters} 
          filters={filters} 
          isAUuser={isAUuser} 
          isHasLessonLibrary={isHasLessonLibrary}
      />}
      {lessonLibraryMode === LessonLibraryEnum.Programs ? <Programs isAUuser={isAUuser} /> :
        <div className={styles.mainView}>
          <div className={mainViewStyle}>
            {filters.length ?
              <div className={styles.searchResult}>
                <div className={styles.searchResultTag}>
                  <span className={styles.resultTitle}>Results for</span>
                  <div className={styles.searchedTags}>
                    {filters.map((tag, index) => {
                      if (tag.includes('Grade') || tag.includes('Tier') || tag.includes('LessonType') || tag.includes('Mood')) {
                        var grade = tag.replace('Grade: ', '').replace('Tier: ', '').replace('LessonType: ', '').replace('Mood: ', '')
                        return <div
                          className={styles.searchTag}
                          key={index}
                          onClick={() => handleRemoveTag(tag)}>
                          <span>{grade}</span>
                          <CloseIcon className={styles.tagClose} />
                        </div>
                      } else if (tag.includes('Keyword')) {
                        const keyword = tag.replace('Keyword: ', '')
                        const isLongTag = keyword.length > 30;
                        return <div
                          className={styles.searchThemeTag}
                          key={index}
                          onClick={() => handleRemoveTag(tag)}
                        >
                          <span>{isLongTag ? `${keyword.slice(0, 30)}...` : keyword}</span>
                          <CloseIcon className={styles.themeTagClose} />
                        </div>
                      }
                      const isLongTag = tag.replace('Skill: ', '').length > 30;
                      return <div
                        className={styles.searchTag}
                        key={tag}
                        onClick={() => handleRemoveTag(tag)}
                      >
                        <div><span>{isLongTag ? `${tag.replace('Skill: ', '').slice(0, 30)}...` : tag.replace('Skill: ', '')}</span></div>
                        <CloseIcon className={styles.tagClose} />
                      </div>
                    })
                    }
                  </div>
                </div>
                <Button onClick={handleResetFilters} className={styles.resetBtn} label={'Reset'}
                  size={'extraSmall'} outline={true} color={'red'}
                  leftIcon={<IconRotating className={styles.resetIcon} />} />
              </div> : null
            }
            {(loading || searching) ? <div className={styles.lazyLoadingView}><Spinner /></div> : <div className={styles.listView}>
              {hasArrayValues(filters) && !hasOnlyGradeFilters ? (
                hasArrayValues(searchedList) ? searchedList.map((item, index) => {
                  return (
                    <Suspense key={index} fallback={<div className={styles.lazyLoadingView}><Spinner /></div>}>
                      <div style={{ position: 'relative' }}>
                        <GradeSearchItem
                          gradeId={item?.mCollectionId ?? ""}
                          gradeName={item?.mCollectionName ?? ""}
                          collectionList={item?.content || []}
                          loading={loading}
                          country={""}
                          isAUuser={isAUuser}
                        />
                      </div>
                    </Suspense>
                  )
                }) : <div className={styles.nodata}>Sorry, We couldn't find any results</div>
              ) : (programList.map((item, index) => {
                return (
                  <Suspense key={index} fallback={<div className={styles.lazyLoadingView}><Spinner /></div>}>
                    <div key={index} style={{ position: 'relative' }}>
                      <GradeItem gradeId={item?.id ?? ""} isAUuser={isAUuser}
                        gradeName={item?.name ?? ""} selected={selected} selectedId={selectedId} loading={loading}
                        handleNoneSelect={() => handleNoneSelect(item?.id)} handleSeleted={handleSelect} handleBackToTop={() => handleBackToTop(item?.id)}
                        loadOthers={loadOtherMCs} setLoadOthers={setLoadOtherMCs} index={index}
                      />
                    </div>
                  </Suspense>
                )
              })
              )}
            </div>}
          </div>
        </div>
      }
    </div>
  )

  return (
    <>
      <Header />
      <div id="lessonlibrary" className={styles.wrapper}>
        <div className={[styles.backdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} onClick={handleClickDrop} />
        <div className={searchViewStyle}>
          <SearchLayout
            focused={focused}
            setFocused={setFocused}
            showGradeMenu={showGradeMenu}
            setShowGradeMenu={setShowGradeMenu}
            showLessonTypeMenu={showLessonTypeMenu}
            setShowLessonTypeMenu={setShowLessonTypeMenu}
            showTierMenu={showTierMenu}
            setShowTierMenu={setShowTierMenu}
            showCompetencyMenu={showCompetencyMenu}
            setShowCompetencyMenu={setShowCompetencyMenu}
            filters={filters}
            search={search}
            gradeOptions={gradeOptions ? gradeOptions : isAUuser ? auAllYearLevels : naAllGradeLevels} lessonTypeOptions={lessonTypeOptions} tierOptions={categoryTiersOptions} competencyOptions={standaloneLessonsSelCompetencies}
            selGrades={grades} selLessonTypes={states.lessonType} selTiers={states.tier} selCompetencys={states.competency}
            handleSelectGrade={handleSelectGrade}
            handleSelectLessonType={(v) => handleStatesChange('lessonType', v)}
            handleSelectTier={(v) => handleStatesChange('tier', v)}
            handleSelectCompetency={(v) => handleStatesChange('competency', v)}
            handleChangeKeyword={handleChangeKeyword}
            handleApplySearch={handleFilterApply}
            isAUuser={isAUuser}
            showSkillMenu={showSkillMenu}
            setShowSkillMenu={setShowSkillMenu}
            showMoodMenu={showMoodMenu}
            setShowMoodMenu={setShowMoodMenu}
            selMood={states.mood}
            handleSelectMood={(v) => handleStatesChange('mood', v)}
            selSkill={states.skill}
            handleSelectSkills={(v) => handleStatesChange('skill', v)}
          />
        </div>
        {(focused || showGradeMenu || showLessonTypeMenu || showCompetencyMenu || showTierMenu || showMoodMenu || showSkillMenu) && <div className={[styles.menuBackdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} />}
        <div className={styles.page}>
          {lessonListView}
        </div>
      </div>
    </>
  );
}

export default LessonLibrary;
